import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { NetworkAddress } from '../types/models/NetworkAddress';
import type { ApiResponseNetworkAddresses } from '../types/api/ApiResponseNetworks';

interface UseNetworkAddressesOptions {
  skip?: boolean;
}

interface UseNetworkAddressesReturn {
  addresses: NetworkAddress[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const extractErrorMessage = (data: ApiResponseNetworkAddresses, status: number): string => {
  if (data.data !== undefined && data.data !== null) {
    const dataRecord = data.data as Record<string, unknown>;
    if (typeof dataRecord.children === 'string') {
      return dataRecord.children;
    }
    // Surface the first string field of a JSend fail payload, if any.
    for (const value of Object.values(dataRecord)) {
      if (typeof value === 'string') {
        return value;
      }
    }
  }
  if (typeof data.message === 'string' && data.message.length > 0) {
    return data.message;
  }
  return `HTTP ${status}`;
};

export const useNetworkAddresses = (id: number, options?: UseNetworkAddressesOptions): UseNetworkAddressesReturn => {
  const skip = options?.skip ?? false;
  const [addresses, setAddresses] = useState<NetworkAddress[]>([]);
  const [loading, setLoading] = useState<boolean>(!skip && id > 0);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchAddresses = useCallback(async () => {
    if (skip || id <= 0) {
      setAddresses([]);
      setLoading(false);
      setError(null);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch(`/datacenters/networks/${id}/addresses`);
      const data = (await response.json()) as ApiResponseNetworkAddresses;

      if (!response.ok || data.status !== 'success') {
        setError(extractErrorMessage(data, response.status));
        setAddresses([]);
        return;
      }

      const payload = data.data;
      if (payload && 'addresses' in payload && Array.isArray(payload.addresses)) {
        setAddresses(payload.addresses);
      } else {
        setAddresses([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching IP addresses');
      setAddresses([]);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, id, skip]);

  useEffect(() => {
    if (!auth.isAuthenticated) {
      return;
    }
    if (skip || id <= 0) {
      setAddresses([]);
      setLoading(false);
      setError(null);
      return;
    }
    fetchAddresses();
  }, [auth.isAuthenticated, fetchAddresses, id, skip]);

  return { addresses, loading, error, refetch: fetchAddresses };
};
